import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import { type GetUserDetailsQuery } from '../queries/getUserDetails.query.generated';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useCanManageUserAccess } from '../hooks/useCanManageUserAccess';
import FeaturePermissionsPanel from './FeaturePermissionsPanel';
import { isSuperAdmin } from '../utils/roleUtils';
import { deriveRooftopEligibility, type EligibilityRooftop } from '../utils/featureEligibility';

type UserDetail = NonNullable<GetUserDetailsQuery['users']>[0];
type UserToCustomer = NonNullable<UserDetail['users_usertocustomers']>[0];

interface UserFeaturesTabProps {
  user: UserDetail;
  refetch: () => void;
}

const UserFeaturesTab: React.FC<UserFeaturesTabProps> = ({ user, refetch }) => {
  const { grantFeatures, revokeFeatures } = useFeatureAccess();
  const canManageUser = useCanManageUserAccess();

  // Only a super admin may edit a super admin's features.
  const canManage = canManageUser(user.default_role);

  const serverFeatureIds = useMemo(
    () => new Set((user.users_usertofeatures ?? []).map(f => f.feature.id)),
    [user],
  );

  // Tiers + enabled services for this user's rooftops — single derivation used
  // for both the tier gate (+ Lite badge) and the service `requires` gate.
  // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
  const { tiers: targetUserTiers, enabledServices } = useMemo(() => {
    const rooftops: EligibilityRooftop[] = ((user as any).users_usertocustomers ?? []).map(
      (utc: UserToCustomer) => ({
        tier: utc.customer?.tier,
        effective_config: (utc.customer as any)?.effective_config,
        // Card #2346: Appraisals relevance comes from trigger ownership on an
        // active contract, which the query selects alongside the config.
        contracts: (utc.customer as any)?.contracts,
      }),
    );
    return deriveRooftopEligibility(rooftops);
  }, [user]);

  // Optimistic overrides
  const [optimisticAdds, setOptimisticAdds] = useState<Set<number>>(new Set());
  const [optimisticRemoves, setOptimisticRemoves] = useState<Set<number>>(new Set());

  // Sync optimistic state with server data
  useEffect(() => {
    setOptimisticAdds(prev => {
      const next = new Set<number>();
      for (const id of prev) {
        if (!serverFeatureIds.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
    setOptimisticRemoves(prev => {
      const next = new Set<number>();
      for (const id of prev) {
        if (serverFeatureIds.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [serverFeatureIds]);

  const userFeatureIds = useMemo(() => {
    const ids = new Set(serverFeatureIds);
    for (const id of optimisticAdds) ids.add(id);
    for (const id of optimisticRemoves) ids.delete(id);
    return ids;
  }, [serverFeatureIds, optimisticAdds, optimisticRemoves]);

  const handleToggle = useCallback(async (featureId: number, enabled: boolean, cascadedIds: number[]) => {
    // Authorization guard (defense-in-depth alongside the disabled UI).
    if (!canManage) return;

    // Optimistically update UI
    if (enabled) {
      setOptimisticAdds(prev => new Set(prev).add(featureId));
      setOptimisticRemoves(prev => { const next = new Set(prev); next.delete(featureId); return next; });
    } else {
      setOptimisticRemoves(prev => {
        const next = new Set(prev).add(featureId);
        for (const childId of cascadedIds) next.add(childId);
        return next;
      });
      setOptimisticAdds(prev => {
        const next = new Set(prev);
        next.delete(featureId);
        for (const childId of cascadedIds) next.delete(childId);
        return next;
      });
    }

    try {
      if (enabled) {
        await grantFeatures(user.id, [featureId]);
      } else {
        await revokeFeatures(user.id, [featureId, ...cascadedIds]);
        refetch();
      }
      refetch();
    } catch (err: any) {
      const allIds = [featureId, ...cascadedIds];
      if (enabled) {
        setOptimisticAdds(prev => { const next = new Set(prev); next.delete(featureId); return next; });
      } else {
        setOptimisticRemoves(prev => {
          const next = new Set(prev);
          for (const id of allIds) next.delete(id);
          return next;
        });
      }
      toast.error(`Failed to update feature: ${err.message}`);
    }
  }, [user.id, canManage, grantFeatures, revokeFeatures, refetch]);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Features</Typography>

        {!canManage && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2, fontStyle: 'italic' }}>
            Only a super admin can change this user's features.
          </Typography>
        )}

        <FeaturePermissionsPanel
          selectedIds={userFeatureIds}
          onToggle={handleToggle}
          disabled={!canManage}
          lockedFeatureNames={isSuperAdmin(user.default_role) ? ['usermanagement'] : []}
          targetUserTiers={targetUserTiers}
          enabledServices={enabledServices}
        />
      </Paper>
    </Box>
  );
};

export default UserFeaturesTab;
